import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const EQUIPMENT_TYPES = [
  { value: "DRY_VAN", label: "Dry Van" },
  { value: "REEFER", label: "Reefer" },
  { value: "FLATBED", label: "Flatbed" },
  { value: "STEP_DECK", label: "Step Deck" },
  { value: "LOWBOY", label: "Lowboy" },
  { value: "TANKER", label: "Tanker" },
  { value: "POWER_ONLY", label: "Power Only" },
  { value: "HOTSHOT", label: "Hotshot" },
];

export default function CarrierLoadAlerts() {
  const loc = useLocation();
  const apiBase = useMemo(() => getApiBase(), []);
  const [carrierEmail, setCarrierEmail] = useState(loc.state?.carrierEmail || "dispatch@bestcarrier.com");

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // New alert form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    originStates: [],
    destStates: [],
    equipmentTypes: [],
    minRateCents: "",
    minDistanceMiles: "",
    maxDistanceMiles: "",
    notifyEmail: true,
    notifySms: false,
    phoneNumber: ""
  });
  const [saving, setSaving] = useState(false);

  async function loadAlerts() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/load-alerts`, {
        headers: { "x-carrier-email": carrierEmail }
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to load alerts");
      const data = await res.json();
      setAlerts(data.alerts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line
  }, [carrierEmail]);

  async function createAlert() {
    if (!formData.name.trim()) {
      setError("Alert name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...formData,
        minRateCents: formData.minRateCents ? parseInt(formData.minRateCents) : null,
        minDistanceMiles: formData.minDistanceMiles ? parseInt(formData.minDistanceMiles) : null,
        maxDistanceMiles: formData.maxDistanceMiles ? parseInt(formData.maxDistanceMiles) : null,
      };
      const res = await fetch(`${apiBase}/load-alerts`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-carrier-email": carrierEmail 
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to create alert");
      setSuccess("Alert created successfully!");
      setShowForm(false);
      setFormData({
        name: "",
        originStates: [],
        destStates: [],
        equipmentTypes: [],
        minRateCents: "",
        minDistanceMiles: "",
        maxDistanceMiles: "",
        notifyEmail: true,
        notifySms: false,
        phoneNumber: ""
      });
      await loadAlerts();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleAlert(alertId, isActive) {
    try {
      const res = await fetch(`${apiBase}/load-alerts/${alertId}`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "x-carrier-email": carrierEmail 
        },
        body: JSON.stringify({ isActive: !isActive })
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to update alert");
      await loadAlerts();
    } catch (e) {
      setError(e.message);
    }
  }

  async function deleteAlert(alertId) {
    if (!window.confirm("Delete this alert?")) return;
    try {
      const res = await fetch(`${apiBase}/load-alerts/${alertId}`, {
        method: "DELETE",
        headers: { "x-carrier-email": carrierEmail }
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to delete alert");
      setSuccess("Alert deleted");
      await loadAlerts();
    } catch (e) {
      setError(e.message);
    }
  }

  function toggleArrayItem(arr, item) {
    if (arr.includes(item)) {
      return arr.filter(x => x !== item);
    }
    return [...arr, item];
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <PageHeader 
        title="🔔 Load Alerts" 
        subtitle="Get notified when matching loads are posted"
      />

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, color: "#666" }}>Carrier Email</div>
          <input 
            value={carrierEmail} 
            onChange={(e) => setCarrierEmail(e.target.value)} 
            style={{ width: 280, padding: 8 }} 
          />
        </div>
        <button onClick={loadAlerts} disabled={loading} style={{ padding: "10px 16px", fontWeight: 700 }}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button 
          onClick={() => setShowForm(!showForm)}
          style={{ 
            padding: "10px 20px", 
            background: "#2563eb", 
            color: "white", 
            border: "none", 
            borderRadius: 8,
            fontWeight: 700,
            cursor: "pointer"
          }}
        >
          {showForm ? "Cancel" : "➕ Create Alert"}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, marginBottom: 16, color: "#991b1b" }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: 12, background: "#d1fae5", borderRadius: 8, marginBottom: 16, color: "#065f46" }}>
          {success}
        </div>
      )}

      {/* Create Alert Form */}
      {showForm && (
        <div style={{ padding: 20, background: "white", borderRadius: 12, border: "1px solid #e5e7eb", marginBottom: 20 }}>
          <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>Create New Alert</h3>
          
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Alert Name *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., TX to Midwest Flatbed"
                style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Origin States (click to select)</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 120, overflowY: "auto", padding: 8, background: "#f9fafb", borderRadius: 6 }}>
                  {US_STATES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({...formData, originStates: toggleArrayItem(formData.originStates, s)})}
                      style={{
                        padding: "4px 8px",
                        fontSize: 12,
                        border: "1px solid",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: formData.originStates.includes(s) ? "#2563eb" : "white",
                        color: formData.originStates.includes(s) ? "white" : "#374151",
                        borderColor: formData.originStates.includes(s) ? "#2563eb" : "#d1d5db"
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Selected: {formData.originStates.length ? formData.originStates.join(", ") : "Any"}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Destination States (click to select)</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 120, overflowY: "auto", padding: 8, background: "#f9fafb", borderRadius: 6 }}>
                  {US_STATES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({...formData, destStates: toggleArrayItem(formData.destStates, s)})}
                      style={{
                        padding: "4px 8px",
                        fontSize: 12,
                        border: "1px solid",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: formData.destStates.includes(s) ? "#2563eb" : "white",
                        color: formData.destStates.includes(s) ? "white" : "#374151",
                        borderColor: formData.destStates.includes(s) ? "#2563eb" : "#d1d5db"
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Selected: {formData.destStates.length ? formData.destStates.join(", ") : "Any"}
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Equipment Types</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {EQUIPMENT_TYPES.map(eq => (
                  <button
                    key={eq.value}
                    type="button"
                    onClick={() => setFormData({...formData, equipmentTypes: toggleArrayItem(formData.equipmentTypes, eq.value)})}
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      border: "1px solid",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: formData.equipmentTypes.includes(eq.value) ? "#2563eb" : "white",
                      color: formData.equipmentTypes.includes(eq.value) ? "white" : "#374151",
                      borderColor: formData.equipmentTypes.includes(eq.value) ? "#2563eb" : "#d1d5db"
                    }}
                  >
                    {eq.label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Selected: {formData.equipmentTypes.length ? formData.equipmentTypes.join(", ") : "Any"}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Min Rate (cents)</label>
                <input
                  type="number"
                  value={formData.minRateCents}
                  onChange={(e) => setFormData({...formData, minRateCents: e.target.value})}
                  placeholder="e.g., 200000 = $2,000"
                  style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Min Distance (mi)</label>
                <input
                  type="number"
                  value={formData.minDistanceMiles}
                  onChange={(e) => setFormData({...formData, minDistanceMiles: e.target.value})}
                  placeholder="e.g., 200"
                  style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Max Distance (mi)</label>
                <input
                  type="number"
                  value={formData.maxDistanceMiles}
                  onChange={(e) => setFormData({...formData, maxDistanceMiles: e.target.value})}
                  placeholder="e.g., 1000"
                  style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={formData.notifyEmail}
                  onChange={(e) => setFormData({...formData, notifyEmail: e.target.checked})}
                />
                <span>📧 Email Notifications</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={formData.notifySms}
                  onChange={(e) => setFormData({...formData, notifySms: e.target.checked})}
                />
                <span>📱 SMS Notifications</span>
              </label>
              {formData.notifySms && (
                <input
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                  placeholder="Phone number"
                  style={{ padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              )}
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button
                onClick={createAlert}
                disabled={saving}
                style={{
                  padding: "12px 24px",
                  background: "#059669",
                  color: "white",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer"
                }}
              >
                {saving ? "Creating..." : "✅ Create Alert"}
              </button>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  padding: "12px 24px",
                  background: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
        <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Your Alerts ({alerts.length})</h3>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading...</div>
        ) : alerts.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔔</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>No alerts yet</div>
            <div style={{ fontSize: 14, marginTop: 8 }}>Create your first alert to get notified when matching loads are posted</div>
          </div>
        ) : (
          <div>
            {alerts.map(alert => (
              <div key={alert.id} style={{ padding: 16, borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <span style={{
                    padding: "4px 10px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: alert.isActive ? "#d1fae5" : "#f3f4f6",
                    color: alert.isActive ? "#065f46" : "#6b7280"
                  }}>
                    {alert.isActive ? "Active" : "Paused"}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{alert.name}</span>
                  <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
                    Triggered {alert.triggerCount || 0} times
                  </span>
                </div>
                
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13, color: "#4b5563" }}>
                  <div>
                    <strong>Origin:</strong> {alert.originStates?.length ? alert.originStates.join(", ") : "Any"}
                  </div>
                  <div>
                    <strong>Dest:</strong> {alert.destStates?.length ? alert.destStates.join(", ") : "Any"}
                  </div>
                  <div>
                    <strong>Equipment:</strong> {alert.equipmentTypes?.length ? alert.equipmentTypes.join(", ") : "Any"}
                  </div>
                  {alert.minRateCents && (
                    <div><strong>Min Rate:</strong> ${(alert.minRateCents / 100).toFixed(0)}</div>
                  )}
                  {alert.minDistanceMiles && (
                    <div><strong>Min Distance:</strong> {alert.minDistanceMiles} mi</div>
                  )}
                  {alert.maxDistanceMiles && (
                    <div><strong>Max Distance:</strong> {alert.maxDistanceMiles} mi</div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    onClick={() => toggleAlert(alert.id, alert.isActive)}
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      background: alert.isActive ? "#fef3c7" : "#d1fae5",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer"
                    }}
                  >
                    {alert.isActive ? "⏸️ Pause" : "▶️ Activate"}
                  </button>
                  <button
                    onClick={() => deleteAlert(alert.id)}
                    style={{
                      padding: "6px 12px",
                      fontSize: 13,
                      background: "#fee2e2",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      color: "#991b1b"
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
